import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'mantine-react-table'
import { Badge } from '@mantine/core'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { findLabelByValue, formatDateForTable, formatDateWithoutTime } from '@/shared/lib/utils'
import { EventTypes, NotificationLogListItem } from '@/shared/api/topli/TopliApi'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { eventTypeColors } from './consts'
import { useNotificationLog } from '@/entities/NotificationLog'

export function useEventsTable(user_id: number) {
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { eventTypesOptions, appointmentTypeOptions } = useTranslatedEnums()
  const debouncedSearch = useDebounce(search, 500)
  const { t } = useTranslation(['userManagement', 'common'])
  const { language } = useLanguageStore()

  const filteredEventTypesOptions = eventTypesOptions.filter(
    item =>
      ![
        EventTypes.LandingFormFeedback,
        EventTypes.LandingFormInfo,
        EventTypes.LandingViewed,
      ].includes(item.value),
  )

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const selectedEvents = (columnFilters.find(f => f.id === 'event_type')?.value ?? []) as number[]

  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    isError: isErrorNotifications,
    isFetching: isFetchingNotifications,
  } = useNotificationLog({
    sort: memoizedSorting,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    user_id,
    search: debouncedSearch,
    events: selectedEvents.length
      ? selectedEvents
      : [EventTypes.Liked, EventTypes.Scheduled, EventTypes.Viewed],
  })

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<NotificationLogListItem>[]>(
    () => [
      {
        header: t('userManagement:client_name'),
        accessorKey: 'client_name',
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.client_name ? (
            <div className='text-sm font-semibold'>{row.original.client_name}</div>
          ) : (
            '-'
          ),
      },
      {
        header: t('common:library_items.event_type.sing'),
        accessorKey: 'event_type',
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: filteredEventTypesOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        Cell: ({ row }) => (
          <Badge size='sm' color={eventTypeColors[row.original.event_type]}>
            {findLabelByValue(filteredEventTypesOptions, row.original.event_type)}
          </Badge>
        ),
      },
      {
        header: t('userManagement:object_link'),
        accessorKey: 'object_link',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.object_link ? (
            <a
              href={row.original.object_link}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-500 hover:underline'
            >
              {t('openLink')}
            </a>
          ) : (
            '-'
          ),
      },
      {
        header: t('userManagement:appointment_date'),
        accessorKey: 'appointment_date',
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.appointment_date
            ? formatDateWithoutTime(row.original.appointment_date, language)
            : '-',
      },
      {
        header: t('common:library_items.appointment_type.sing'),
        accessorKey: 'appointment_type',
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.appointment_type ? (
            <Badge size='sm'>
              {findLabelByValue(appointmentTypeOptions, row.original.appointment_type)}
            </Badge>
          ) : (
            '-'
          ),
      },
      {
        header: t('userManagement:created_at'),
        enableColumnFilter: false,
        accessorKey: 'created_at',
        Cell: ({ row }) => formatDateForTable(row.original.created_at, language),
      },
    ],
    [],
  )

  return {
    fetchedNotifications: notifications?.data?.filter(item => item.event_type !== 4) ?? [],
    totalRowCount: notifications?.total ?? 0,
    columns,
    pagination,
    isErrorNotifications,
    isFetchingNotifications,
    isLoadingNotifications,
    sorting,
    setPagination,
    setSorting,
    handleSearchChange,
    columnFilters,
    setColumnFilters,
  }
}
